import { useState } from 'react';
import Seo from '@/components/Seo';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, User, ArrowLeft, Loader2, Building2, ShoppingBag, CheckCircle, Phone, MessageCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import RotatingBackground from '@/components/RotatingBackground';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { API_URL } from '@/config/api';

// BOH Roles that have access to admin portal
const BOH_ROLES = [
  'super_admin',
  'manager',
  'operations_manager',
  'kitchen',
  'fulfilment',
  'customer_support',
  'finance',
  'driver',
  'admin',
  'staff'
];

export default function AppAuth() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState('login'); // login | register
  const [portal, setPortal] = useState('customer'); // customer | boh
  const [method, setMethod] = useState('email'); // email | phone (customer only)
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordPhone, setForgotPasswordPhone] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  // Magic-link + phone OTP verification + referral / voucher QR
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get('ref') || '';
  const voucherCode = refParam === 'voucher' ? (searchParams.get('code') || '').trim().toUpperCase() : '';
  const referralCode = refParam && refParam !== 'voucher' ? refParam : '';
  const [magicSent, setMagicSent] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpPhone, setOtpPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const from = location.state?.from || '/';
  const postAuthDest = voucherCode ? `/profile?redeem=${encodeURIComponent(voucherCode)}` : from;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (portal === 'boh') {
        // BOH Login - use admin login endpoint
        const response = await fetch(`${API_URL}/api/admin/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          })
        });

        if (!response.ok) {
          let errorMessage = 'Login failed';
          try {
            const error = await response.json();
            errorMessage = error.detail || errorMessage;
          } catch {
            // Response wasn't JSON
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        // Validate BOH role
        if (!BOH_ROLES.includes(data.user.role)) {
          throw new Error('This account does not have Back of House access.');
        }

        // Check if password change is required
        if (data.must_change_password) {
          // Store token temporarily for password change
          login(data.access_token, data.user);
          toast.info('Please change your password to continue');
          navigate('/admin', { replace: true });
          return;
        }

        login(data.access_token, data.user);
        toast.success('Welcome to Back of House Portal');
        navigate('/admin', { replace: true });

      } else {
        // Customer Login/Register — email or phone method
        let endpoint, body, missingMsg;
        if (method === 'phone') {
          endpoint = mode === 'login' ? '/api/auth/login-phone' : '/api/auth/register-phone';
          body = mode === 'login'
            ? { phone: formData.phone, password: formData.password }
            : { name: formData.name, phone: formData.phone, password: formData.password };
        } else {
          endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
          body = mode === 'login'
            ? { email: formData.email, password: formData.password }
            : { name: formData.name, email: formData.email, password: formData.password };
        }

        const response = await fetch(`${API_URL}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body)
        });

        if (!response.ok) {
          let errorMessage = `${mode === 'login' ? 'Login' : 'Registration'} failed`;
          try {
            const error = await response.json();
            errorMessage = error.detail || errorMessage;
          } catch {
            // Response wasn't JSON
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        login(data.access_token, data.user);

        // Apply referral code on registration (rewards credit on first order)
        if (mode === 'register' && referralCode) {
          try {
            await fetch(`${API_URL}/api/referral/apply`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${data.access_token}` },
              credentials: 'include',
              body: JSON.stringify({ code: referralCode })
            });
          } catch { /* non-blocking */ }
        }

        // Phone registration → verify number via WhatsApp OTP
        if (mode === 'register' && method === 'phone') {
          setOtpPhone(formData.phone);
          try {
            await fetch(`${API_URL}/api/auth/phone/send-otp`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone: formData.phone, purpose: 'verify' })
            });
          } catch { /* ignore */ }
          setOtpStep(true);
          setLoading(false);
          return;
        }

        toast.success(mode === 'login' ? 'Welcome back!' : 'Account created!');
        navigate(postAuthDest, { replace: true });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Magic-link (passwordless) login via email
  const handleMagicLink = async () => {
    if (!formData.email.trim()) { toast.error('Enter your email first'); return; }
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/auth/magic-link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email.trim() })
      });
      setMagicSent(true);
    } catch { setMagicSent(true); }
    finally { setLoading(false); }
  };

  // Verify phone OTP after registration
  const handleVerifyOtp = async () => {
    if (otpCode.trim().length < 4) { toast.error('Enter the code from WhatsApp'); return; }
    setOtpLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/phone/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: otpPhone, code: otpCode.trim() })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Verification failed');
      toast.success('Phone verified! 🎉');
      navigate(postAuthDest, { replace: true });
    } catch (e) { toast.error(e.message); }
    finally { setOtpLoading(false); }
  };

  const handleResendOtp = async () => {
    try {
      await fetch(`${API_URL}/api/auth/phone/send-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: otpPhone, purpose: 'verify' })
      });
      toast.success('New code sent via WhatsApp');
    } catch { toast.error('Could not resend'); }
  };


  // Handle forgot password (email or WhatsApp/phone)
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const isPhone = method === 'phone';
    if (isPhone && !forgotPasswordPhone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (!isPhone && !forgotPasswordEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setForgotPasswordLoading(true);
    try {
      const endpoint = isPhone ? '/api/auth/forgot-password-phone' : '/api/auth/forgot-password';
      const body = isPhone
        ? { phone: forgotPasswordPhone.trim() }
        : { email: forgotPasswordEmail.trim(), user_type: 'customer' };
      await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      // Always show success to prevent enumeration
      setForgotPasswordSent(true);
    } catch {
      setForgotPasswordSent(true);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    // Google auth always goes to customer portal
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (otpStep) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50 p-5" data-testid="otp-step">
        <div className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-secondary-900">Verify your number</h2>
          <p className="text-muted-foreground text-sm mt-2 mb-6">We sent a 6-digit code via WhatsApp to <strong>{otpPhone}</strong></p>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
            placeholder="••••••"
            className="w-full h-16 text-center text-3xl font-bold tracking-[0.5em] bg-secondary-50 border border-border rounded-2xl mb-4 focus:ring-2 focus:ring-primary/30"
            data-testid="otp-input"
          />
          <button
            onClick={handleVerifyOtp}
            disabled={otpLoading}
            className="w-full h-14 bg-primary text-white rounded-2xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="otp-verify-btn"
          >
            {otpLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verify number'}
          </button>
          <div className="flex items-center justify-center gap-4 mt-4 text-sm">
            <button onClick={handleResendOtp} className="text-primary font-medium" data-testid="otp-resend-btn">Resend code</button>
            <span className="text-border">|</span>
            <button onClick={() => navigate(postAuthDest, { replace: true })} className="text-muted-foreground" data-testid="otp-skip-btn">Skip for now</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="app-auth">
      <Seo title="Sign In & Register" path="/auth" description="Sign in or create your free GC3H Online Canteen account to order fresh meals, top up your digital wallet, redeem gift vouchers and track every order. Register with your email or phone in under a minute." />
      {/* Left Side - Rotating Background (Desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <RotatingBackground 
          interval={15000} 
          overlay={true} 
          overlayColor="bg-secondary-900/50"
          className="absolute inset-0"
          variant="customer"
        >
          <div className="h-full flex items-center justify-center p-12">
            <div className="text-center">
              <img 
                src="https://gc3h.co.za/wp-content/uploads/2022/02/gallagher-convention-centre-catering.png"
                alt="GC3H"
                className="h-28 w-auto mx-auto mb-8"
              />
              <h2 className="font-heading text-4xl font-semibold text-white mb-4">
                {portal === 'boh' ? 'Back of House' : 'Welcome'}
              </h2>
              <p className="text-white/80 text-lg max-w-sm mx-auto">
                {portal === 'boh' 
                  ? 'Manage orders, menus and kitchen operations' 
                  : 'Order Smart. Eat Fresh. Delivered Right.'
                }
              </p>
            </div>
          </div>
        </RotatingBackground>
      </div>

      {/* Right Side - Form */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="w-full lg:w-1/2 min-h-screen bg-background flex flex-col"
      >
        {/* Header */}
        <div className="px-5 pt-6">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate('/')}
            className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center mb-8 lg:hidden"
          >
            <ArrowLeft className="h-5 w-5" />
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-6"
          >
            <img 
              src="https://gc3h.co.za/wp-content/uploads/2022/02/gallagher-convention-centre-catering.png"
              alt="GC3H"
              className="h-16 w-auto mx-auto mb-6 lg:hidden"
            />
            <h1 className="font-heading text-3xl font-semibold text-secondary-900 mb-2">
              {portal === 'boh' ? 'Back of House Login' : (mode === 'login' ? 'Welcome Back' : 'Create Account')}
            </h1>
            <p className="text-muted-foreground">
              {portal === 'boh' 
                ? 'Sign in to access the admin portal' 
                : (mode === 'login' ? 'Sign in to continue ordering' : 'Sign up to start ordering')
              }
            </p>
          </motion.div>
        </div>

      {/* Portal Selection */}
      <div className="px-5 mb-6">
        <p className="text-sm font-medium text-secondary-700 mb-3 text-center">Log in as:</p>
        <div className="flex gap-3">
          <motion.button
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={() => { setPortal('customer'); setMode('login'); }}
            className={`flex-1 h-14 rounded-2xl font-medium flex items-center justify-center gap-2 transition-all ${
              portal === 'customer'
                ? 'bg-primary text-white shadow-lg'
                : 'bg-white border border-border text-secondary-600 hover:border-primary/50'
            }`}
            data-testid="portal-customer"
          >
            <ShoppingBag className="h-5 w-5" />
            Customer
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={() => { setPortal('boh'); setMode('login'); }}
            className={`flex-1 h-14 rounded-2xl font-medium flex items-center justify-center gap-2 transition-all ${
              portal === 'boh'
                ? 'bg-secondary-900 text-white shadow-lg'
                : 'bg-white border border-border text-secondary-600 hover:border-secondary-400'
            }`}
            data-testid="portal-boh"
          >
            <Building2 className="h-5 w-5" />
            Back of House
          </motion.button>
        </div>
      </div>

      {/* Email / Phone method toggle — customer only */}
      {portal === 'customer' && (
        <div className="px-5 mb-4" data-testid="method-toggle">
          <div className="flex gap-2 bg-muted rounded-2xl p-1">
            <button
              type="button"
              onClick={() => setMethod('email')}
              className={`flex-1 h-11 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                method === 'email' ? 'bg-white shadow text-secondary-900' : 'text-secondary-500'
              }`}
              data-testid="method-email"
            >
              <Mail className="h-4 w-4" />
              Email
            </button>
            <button
              type="button"
              onClick={() => setMethod('phone')}
              className={`flex-1 h-11 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                method === 'phone' ? 'bg-white shadow text-secondary-900' : 'text-secondary-500'
              }`}
              data-testid="method-phone"
            >
              <Phone className="h-4 w-4" />
              Phone
            </button>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="px-5">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name field - only for customer registration */}
          <AnimatePresence mode="wait">
            {mode === 'register' && portal === 'customer' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Full Name"
                    className="w-full h-14 pl-12 pr-4 bg-white border border-border rounded-2xl focus:ring-2 focus:ring-primary/20"
                    required={mode === 'register'}
                    data-testid="name-input"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Email input — email method or BOH portal */}
          {(method === 'email' || portal === 'boh') && (
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="Email"
                className="w-full h-14 pl-12 pr-4 bg-white border border-border rounded-2xl focus:ring-2 focus:ring-primary/20"
                required
                data-testid="email-input"
              />
            </div>
          )}

          {/* Phone input — phone method, customer only */}
          {method === 'phone' && portal === 'customer' && (
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="Phone number (e.g. 082 123 4567)"
                className="w-full h-14 pl-12 pr-4 bg-white border border-border rounded-2xl focus:ring-2 focus:ring-primary/20"
                required
                data-testid="phone-input"
              />
            </div>
          )}

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              placeholder="Password"
              className="w-full h-14 pl-12 pr-12 bg-white border border-border rounded-2xl focus:ring-2 focus:ring-primary/20"
              required
              data-testid="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Eye className="h-5 w-5 text-muted-foreground" />
              )}
            </button>
          </div>

          {/* Forgot Password Link - only show on login mode */}
          {mode === 'login' && (
            <div className="text-right -mt-2">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-primary hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
          )}

          <motion.button
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className={`w-full h-14 rounded-2xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 ${
              portal === 'boh' 
                ? 'bg-secondary-900 text-white' 
                : 'bg-primary text-white'
            }`}
            data-testid="submit-btn"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              portal === 'boh' 
                ? 'Sign In to BOH' 
                : (mode === 'login' ? 'Sign In' : 'Create Account')
            )}
          </motion.button>
        </form>

        {/* Magic-link login — customer email login only */}
        {portal === 'customer' && method === 'email' && mode === 'login' && (
          <div className="mt-3 text-center">
            {magicSent ? (
              <p className="text-sm text-green-600" data-testid="magic-sent-msg">
                ✓ If that email exists, a one-tap login link is on its way.
              </p>
            ) : (
              <button
                type="button"
                onClick={handleMagicLink}
                disabled={loading}
                className="text-sm text-primary font-medium inline-flex items-center gap-1.5"
                data-testid="magic-link-btn"
              >
                <Mail className="h-4 w-4" /> Email me a login link instead
              </button>
            )}
          </div>
        )}

        {/* Google Auth - only for customers */}
        {portal === 'customer' && (
          <>
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-4 bg-background text-muted-foreground text-sm">or</span>
              </div>
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={handleGoogleAuth}
              className="w-full h-14 bg-white border border-border rounded-2xl font-medium flex items-center justify-center gap-3"
              data-testid="google-btn"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </motion.button>

            {/* Toggle Mode - only for customers */}
            <p className="text-center text-muted-foreground mt-8 pb-8">
              {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button"
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                className="text-primary font-semibold"
              >
                {mode === 'login' ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </>
        )}

        {/* BOH Info */}
        {portal === 'boh' && (
          <div className="mt-8 pb-8">
            <div className="bg-secondary-100 rounded-2xl p-4 text-center">
              <p className="text-sm text-secondary-600">
                BOH access is invite-only. Contact your administrator if you need access.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* SEO / informational content — what the canteen offers and what you can do after signing in */}
      <section className="px-5 pb-10 max-w-xl mx-auto w-full text-sm text-muted-foreground space-y-3" data-testid="auth-seo-content">
        <h2 className="text-base font-semibold text-secondary-900 dark:text-white">Fresh, chef-prepared meals — ordered online</h2>
        <p>
          GC3H Online Canteen is the official ordering platform for the Gallagher Convention Centre canteen.
          Signing in gives you a faster, simpler way to order the freshly cooked meals our kitchen prepares each day —
          without queuing. Browse the daily menu, see the meal of the day and today's specials, choose your sides,
          and pay securely in a few taps. Order before the 9AM cut-off and collect your food with a simple QR code,
          or have it delivered to your office.
        </p>
        <h3 className="font-semibold text-secondary-900 dark:text-white">What you can do with an account</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Order daily meals, drinks and snacks</strong> from a menu that updates every day.</li>
          <li><strong>Top up a digital wallet</strong> so checkout is instant and cashless — great for busy lunch breaks.</li>
          <li><strong>Buy and redeem gift vouchers</strong> for birthdays, thank-yous and celebrations, delivered as a branded PDF.</li>
          <li><strong>Earn loyalty rewards and referral bonuses</strong> — invite colleagues and earn free lunches.</li>
          <li><strong>Track every order</strong> from kitchen to collection, with WhatsApp and email updates.</li>
          <li><strong>Order office platters</strong> in bulk for meetings, workshops and corporate events.</li>
        </ul>
        <h3 className="font-semibold text-secondary-900 dark:text-white">Signing in is quick and secure</h3>
        <p>
          Create your free account with either your email address or your South African mobile number. Phone sign-ups are
          verified with a one-time SMS code, and password resets are sent straight to your phone or inbox. Your details are
          protected and never shared. If you're a member of the back-of-house team, use your invite from an administrator to
          access the staff dashboard. Ready to eat fresh? Sign in above to place your first order.
        </p>
        <h3 className="font-semibold text-secondary-900 dark:text-white">How ordering works</h3>
        <p>
          Once you're signed in, choosing your food takes less than a minute. Open the menu, tap the dishes you want, pick any
          sides, and add them to your basket. Place your order before the 9AM cut-off to enjoy your meal the same day. When your
          food is ready you'll receive a notification, and you simply show your unique collection QR code at the counter — no cash,
          no queue, no fuss. Prefer delivery? Choose your office or a saved address at checkout and we'll bring it to you.
        </p>
        <p>
          Regular customers love the digital wallet: top up once and every future order is a single tap. You'll also build up
          loyalty rewards as you order, and every friend you invite who places a qualifying order earns you credit toward a free
          lunch. Whether you're grabbing a quick weekday meal, treating a colleague to a gift voucher, or arranging platters for a
          big meeting, GC3H Online Canteen keeps it fresh, fast and completely hassle-free.
        </p>
        <h3 className="font-semibold text-secondary-900 dark:text-white">Frequently asked questions</h3>
        <p>
          <strong>Do I need an account to order?</strong> Yes — a free account keeps your orders, wallet balance,
          vouchers and rewards safe, and lets us notify you the moment your food is ready.
        </p>
        <p>
          <strong>Where do you deliver?</strong> We deliver to offices, warehouses and business parks around Midrand,
          Waterfall, Kyalami and Sunninghill, and collection at the Gallagher Convention Centre canteen is always free.
        </p>
        <p>
          <strong>How do I pay?</strong> Pay securely by card, or top up your digital wallet once and check out with a
          single tap on every future order. Gift voucher codes can be redeemed straight into your wallet.
        </p>
        <p>
          <strong>I forgot my password — now what?</strong> Tap "Forgot password" on the sign-in form and we'll send a
          secure reset link to your email inbox or straight to your WhatsApp if you registered with a phone number. The
          link brings you right back to a fresh password in under a minute.
        </p>
        <h3 className="font-semibold text-secondary-900 dark:text-white">Trusted by the Gallagher community</h3>
        <p>
          Hundreds of staff, exhibitors and visitors at Gallagher Convention Centre use GC3H Online Canteen every week to
          skip the lunchtime queue. Because orders come in before 9AM, the kitchen cooks exactly what's needed — fresher
          food, less waste, and your meal reserved with your name on it. Join them: create your free account above and
          your first order can be on its way today.
        </p>
      </section>
      </motion.div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => {
        setShowForgotPassword(open);
        if (!open) {
          setForgotPasswordEmail('');
          setForgotPasswordPhone('');
          setForgotPasswordSent(false);
        }
      }}>
        <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {method === 'phone' ? <MessageCircle className="h-5 w-5 text-green-600" /> : <Mail className="h-5 w-5 text-primary" />}
              {!forgotPasswordSent ? 'Reset Your Password' : (method === 'phone' ? 'Check WhatsApp' : 'Check Your Email')}
            </DialogTitle>
            <DialogDescription>
              {!forgotPasswordSent 
                ? (method === 'phone'
                    ? "Enter your phone number and we'll send a reset link via WhatsApp."
                    : "Enter your email and we'll send you a password reset link.")
                : (method === 'phone'
                    ? "If an account exists with that number, a reset link has been sent via WhatsApp."
                    : "If an account exists with that email, you'll receive a reset link.")
              }
            </DialogDescription>
          </DialogHeader>
          {!forgotPasswordSent ? (
            <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
              {method === 'phone' ? (
                <div className="space-y-2">
                  <Label htmlFor="forgotPhone">Phone Number</Label>
                  <Input
                    id="forgotPhone"
                    type="tel"
                    placeholder="082 123 4567"
                    value={forgotPasswordPhone}
                    onChange={(e) => setForgotPasswordPhone(e.target.value)}
                    className="w-full"
                    data-testid="forgot-password-phone-input"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="forgotEmail">Email Address</Label>
                  <Input
                    id="forgotEmail"
                    type="email"
                    placeholder="your@email.com"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="w-full"
                    data-testid="forgot-password-email-input"
                  />
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={forgotPasswordLoading}
                  data-testid="forgot-password-submit-btn"
                >
                  {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="text-center py-6">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">
                If an account exists with <strong>{method === 'phone' ? forgotPasswordPhone : forgotPasswordEmail}</strong>, a password reset link has been sent.
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                {method === 'phone'
                  ? 'The link will expire in 1 hour. Check your SMS (or WhatsApp) messages.'
                  : "The link will expire in 1 hour. Check your spam folder if you don't see it."}
              </p>
              <Button 
                variant="outline"
                onClick={() => setShowForgotPassword(false)}
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
